/**
 * Shared types, constants, and components used by SchemaManager and SchemaSaveDialog.
 */
import React from 'react';
import {
  Box,
  Typography,
  Chip,
  TextField,
  Tooltip,
  IconButton,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Edit as EditIcon,
  Restore as RestoreIcon,
  InfoOutlined as InfoIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Update as UpdateIcon,
} from '@mui/icons-material';
import { ABBR_LEGEND } from '../../utils/schemaNameGenerator';

// ---------------------------------------------------------------------------
// Shared type (matches SchemaData['settings'] in both files)
// ---------------------------------------------------------------------------
export interface SchemaSettings {
  hasHeader: boolean;
  useStreetDistances: boolean;
  standard: 'ISO' | 'EN';
}

export const DEFAULT_SETTINGS: SchemaSettings = {
  hasHeader: true,
  useStreetDistances: true,
  standard: 'ISO',
};

// ---------------------------------------------------------------------------
// isDuplicateName helper
// ---------------------------------------------------------------------------
export function isDuplicateSchemaName(
  schemaName: string,
  schemas: Array<{ name: string; id?: string }>,
  saveMode: 'update' | 'new',
  loadedSchema: { name: string; id?: string } | undefined
): boolean {
  return (
    schemaName.trim() !== '' &&
    schemas.some(s => s.name === schemaName.trim()) &&
    !(saveMode === 'update' && schemaName.trim() === loadedSchema?.name)
  );
}

// ---------------------------------------------------------------------------
// SaveModeToggle — the "Update existing / Save as new" ToggleButtonGroup
// ---------------------------------------------------------------------------
interface SaveModeToggleProps {
  saveMode: 'update' | 'new';
  loadedSchemaName: string;
  onChange: (mode: 'update' | 'new') => void;
  /** When true, renders a `sx={{ mb: 0 }}` on the group (SchemaManager variant) */
  noBottomMarginOnGroup?: boolean;
}

export const SaveModeToggle: React.FC<SaveModeToggleProps> = ({
  saveMode,
  loadedSchemaName,
  onChange,
  noBottomMarginOnGroup = false,
}) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
      How would you like to save?
    </Typography>
    <ToggleButtonGroup
      exclusive
      value={saveMode}
      onChange={(_, v) => { if (v) onChange(v); }}
      size="small"
      fullWidth
      sx={noBottomMarginOnGroup ? { mb: 0 } : undefined}
    >
      <ToggleButton value="update" sx={{ gap: 0.75, py: 1.25, textTransform: 'none' }}>
        <UpdateIcon sx={{ fontSize: 16 }} />
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            Update existing
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            Overwrite "{loadedSchemaName}"
          </Typography>
        </Box>
      </ToggleButton>
      <ToggleButton value="new" sx={{ gap: 0.75, py: 1.25, textTransform: 'none' }}>
        <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            Save as new
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            Keep original, create a copy
          </Typography>
        </Box>
      </ToggleButton>
    </ToggleButtonGroup>
    <Divider sx={{ mt: 2.5 }} />
  </Box>
);

// ---------------------------------------------------------------------------
// SchemaNameField — the name input with auto/custom chip and revert button
// ---------------------------------------------------------------------------
interface SchemaNameFieldProps {
  schemaName: string;
  isNameManuallyEdited: boolean;
  isDuplicateName: boolean;
  onChangeName: (value: string) => void;
  onRevert: () => void;
  /** Extra sx applied to the outer Box wrapper */
  wrapperSx?: object;
}

const AbbrLegendTooltipContent: React.FC = () => (
  <Box sx={{ p: 0.25 }}>
    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
      Abbreviation Legend
    </Typography>
    <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%' }}>
      <Box component="tbody">
        {ABBR_LEGEND.map(({ abbr, label }) => (
          <Box component="tr" key={abbr}>
            <Box component="td" sx={{ pr: 1.5, pb: 0.4, verticalAlign: 'middle' }}>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  px: 0.5,
                  borderRadius: 0.5,
                }}
              >
                {abbr}
              </Typography>
            </Box>
            <Box component="td" sx={{ pb: 0.4, verticalAlign: 'middle' }}>
              <Typography variant="caption">{label}</Typography>
            </Box>
          </Box>
        ))}
        <Box component="tr">
          <Box
            component="td"
            colSpan={2}
            sx={{ pt: 0.75, borderTop: '1px solid rgba(255,255,255,0.2)' }}
          >
            <Typography variant="caption" sx={{ fontStyle: 'italic', opacity: 0.8 }}>
              Number = Excel column position (1-indexed)<br />
              Leg fields grouped: L1(OC:3, DC:4, TM:5)<br />
              e.g. Wt:2 · L1(OC:3, DC:4, TM:5) · L2(OC:6, DC:7, TM:8)
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

export const SchemaNameField: React.FC<SchemaNameFieldProps> = ({
  schemaName,
  isNameManuallyEdited,
  isDuplicateName,
  onChangeName,
  onRevert,
  wrapperSx,
}) => (
  <Box sx={wrapperSx}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Schema Name
      </Typography>
      <Tooltip arrow placement="right" title={<AbbrLegendTooltipContent />}>
        <InfoIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'default' }} />
      </Tooltip>
      {isNameManuallyEdited ? (
        <Chip
          icon={<EditIcon sx={{ fontSize: '12px !important' }} />}
          label="Custom"
          size="small"
          variant="outlined"
          sx={{ height: 20, fontSize: '0.65rem', borderColor: 'grey.400', color: 'text.secondary' }}
        />
      ) : (
        <Chip
          icon={<AutoAwesomeIcon sx={{ fontSize: '12px !important' }} />}
          label="Auto-suggested"
          size="small"
          color="primary"
          variant="outlined"
          sx={{ height: 20, fontSize: '0.65rem' }}
        />
      )}
    </Box>

    <TextField
      fullWidth
      size="small"
      value={schemaName}
      onChange={e => onChangeName(e.target.value)}
      placeholder="e.g. Wt:2 · L1(OC:3, DC:4, TM:5) · L2(OC:6, DC:7, TM:8)"
      error={isDuplicateName}
      helperText={(() => {
        if (isDuplicateName) return 'A schema with this name already exists — saving will overwrite it.';
        if (isNameManuallyEdited) return 'Editing the auto-suggested name.';
        return 'Auto-generated from your mapping. Hover ⓘ to see abbreviation legend.';
      })()}
      InputProps={{
        endAdornment: isNameManuallyEdited ? (
          <InputAdornment position="end">
            <Tooltip title="Revert to auto-suggested name">
              <IconButton size="small" onClick={onRevert} edge="end">
                <RestoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ) : undefined,
      }}
    />
  </Box>
);
